/**
 * @copyright Copyright (c) Open-Xchange GmbH, Germany <info@open-xchange.com>
 * @license AGPL-3.0
 *
 * This code is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with OX App Suite. If not, see <https://www.gnu.org/licenses/agpl-3.0.txt>.
 *
 * Any use of the work other than as authorized under this license or copyright law is prohibited.
 */

// Polyfill bootstrap for the main UI (browser): installs
// `@js-temporal/polyfill` on `globalThis` *only when native `Temporal` is
// unavailable*. The `await import()` lets Vite code-split the polyfill into
// its own chunk that's only fetched at runtime when the `if` branch is taken
// — so browsers with native `Temporal` pay zero bundle cost. Native support
// starts at Chromium 144 and Firefox 139, so the branch is still taken by
// Chromium-based browsers that lag upstream — Samsung Internet in particular.
// https://caniuse.com/?search=Temporal
//
// This is deliberately an exported *function*, not a side-effecting module
// with top-level await. A side-effecting bootstrap module cannot enforce its
// own ordering: an async module (one with top-level await) does not block its
// *siblings* in the importing module's dependency list — they evaluate while
// it is still suspended. So
//
//   import '@/io.ox/core/tempo/polyfill'   // suspends at the await
//   import { tempo } from '@/tempo'        // evaluates anyway -> throws
//
// silently failed on every browser without native `Temporal`. The caller must
// therefore `await installTemporalPolyfill()` before *importing* anything that
// pulls in `@/tempo` — see `@/precore`, which awaits it before the dynamic
// `import('@/main.js')`.
//
// This file is intentionally *not* used by e2e: `e2e/polyfill.js` is a static,
// unconditional import of the polyfill, because Mocha's sync `require()` of
// test files rejects any ESM with top-level await.
export async function installTemporalPolyfill () {
  if (globalThis.Temporal) return
  globalThis.Temporal = (await import('@js-temporal/polyfill')).Temporal
}
